import { useMemo, type CSSProperties } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useCollectionBinding,
  useTokens,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio, getContrastTextColor } from '../utils/luminance';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const imageDef: SlotGroupDefinition = {
  label: 'Feature Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[4].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[4].alt_text',
      defaultValue: 'Community amenities',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 4,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'Select a photo of a marquee amenity — the fitness center, pool, or clubhouse.',
    context:
      'Large feature image next to the "Discover all the amenities" intro copy. Should be a distinct amenity photo, not the property featured image.',
    sourceCollection: 'photo_gallery',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Explore Amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function DiscoverAmenities() {
  useRevealStyles();
  const revealRef = useScrollReveal<HTMLDivElement>();

  const image = useSlotGroup('discoveramenities.image', imageDef);
  const cta = useSlotGroup('discoveramenities.cta', ctaDef);

  const amenities = useCollectionBinding<Amenity>('discoveramenities.items', {
    collection: 'amenities',
    defaultLimit: 10,
  });

  // Body copy + amenities list read against `--vibe-color-surface`; the default
  // `--vibe-color-text` is paired with the page background, so a dark `surface`
  // (property_color_3) leaves the copy dark-on-dark. Keep the themed text when it
  // clears WCAG AA against surface; otherwise fall back to a luminance-picked
  // black/white. Mirrors the banner-text fix in AmenitiesFeatures.
  const tokens = useTokens();
  const bodyTextStyle = useMemo<CSSProperties | undefined>(() => {
    const surface = tokens?.color?.surface;
    if (!surface) return undefined;
    const themed = tokens?.color?.text;
    const color = themed && contrastRatio(surface, themed) >= 4.5 ? themed : getContrastTextColor(surface);
    return { ['--da-body-text' as string]: color } as CSSProperties;
  }, [tokens]);

  return (
    <section className="w-full bg-[var(--vibe-color-surface)] overflow-hidden" style={bodyTextStyle}>
      <div
        ref={revealRef}
        className="reveal-fade-up grid grid-cols-1 md:grid-cols-2 items-stretch"
      >
        {/* ── Text column (kept within readable page gutters) ── */}
        <div className="flex flex-col justify-center pt-8 pb-0 md:pt-6 md:pb-3 lg:pt-12 lg:pb-5 px-4 sm:px-6 md:px-8 lg:px-12">
          <div className="w-full md:max-w-2xl md:ml-auto md:pr-10 lg:pr-16">
          <RichTextContent
            path="discoveramenities.heading"
            label="Heading"
            defaultValue="<p>Discover All The Amenities</p>"
            as="h2"
            className="font-heading text-4xl md:text-5xl font-bold text-[var(--vibe-color-primary)] leading-[1.1] uppercase mb-6 md:mb-3"
          />

          <RichTextContent
            path="discoveramenities.body"
            label="Body Text"
            defaultValue="<p>Our community offers premium amenities to enhance your lifestyle. Start your day with a workout in the fitness center, then relax by the resort-style pool or enjoy the landscaped courtyards. Experience comfort, convenience, and style — your ideal home.</p>"
            as="div"
            className="font-body text-base text-[color:var(--da-body-text,var(--vibe-color-text))] leading-relaxed [&_a]:underline"
          />

          {/* Divider — extends past the text gutters so it meets the image on the right */}
          <hr className="my-4 md:my-4 -mr-4 sm:-mr-6 md:-mr-[4.5rem] lg:-mr-28 border-0 border-t border-[color-mix(in_srgb,var(--vibe-color-primary)_35%,transparent)]" />

          {/* Amenities list */}
          {amenities.length > 0 && (
            <ul className="flex flex-col gap-y-2 md:gap-y-2 mb-0 md:mb-5">
              {amenities.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 font-body text-base text-[color:var(--da-body-text,var(--vibe-color-text))] leading-[1.25] md:leading-normal"
                >
                  <span aria-hidden="true" className="text-[var(--vibe-color-primary)] font-bold leading-[1.25] md:leading-6">
                    +
                  </span>
                  <span>{item.title || ''}</span>
                </li>
              ))}
            </ul>
          )}
          </div>
        </div>

        {/* ── Image column (full bleed on desktop; image hidden on mobile) ── */}
        <div className="relative w-full h-full overflow-hidden md:min-h-[440px] lg:min-h-[520px]">
          <EditableSlotGroup
            groupPath="discoveramenities.image"
            definition={imageDef}
            className="hidden md:block absolute inset-0"
          >
            {image.image ? (
              <VibesitesImage
                url={image.image}
                alt={image.alt || 'Community amenities'}
                className="absolute inset-0 w-full h-full object-cover"
                sizes="(min-width: 768px) 50vw, 100vw"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-background)]" />
            )}
          </EditableSlotGroup>

          {/* CTA band: static over the light surface on mobile; semi-opaque
              band pinned over the image on desktop.
              On mobile the list mb and text-col pb are zeroed, so the band owns
              the full vertical gap — symmetric 2.05rem top/bottom (60% smaller
              than the previous 5.125rem even spacing). */}
          <div className="relative md:absolute md:inset-x-0 md:bottom-0 flex justify-center px-6 py-[2.05rem] md:py-9 md:bg-[color-mix(in_srgb,var(--vibe-color-primary)_85%,transparent)]">
            <EditableSlotGroup groupPath="discoveramenities.cta" definition={ctaDef}>
              <a
                href={cta.url || '/amenities'}
                target={cta._target || '_self'}
                className="da-cta inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-10 lg:py-3.5 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] transition-all"
              >
                {cta.label || 'Explore Amenities'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* Mobile: solid primary pill — matches the site-wide CTA treatment.
           The image is hidden on mobile, so the CTA sits over the light surface. */
        .da-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
        }
        .da-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }

        /* Desktop: ghost button — transparent fill, textOnPrimary border + text,
           so it reads against the semi-opaque primary band over the image. */
        @media (min-width: 768px) {
          .da-cta {
            background: transparent;
            border: 1.5px solid color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 80%, transparent);
          }
          .da-cta:hover {
            background: color-mix(in srgb, var(--vibe-color-text-on-primary, #fff) 15%, transparent);
            border-color: var(--vibe-color-text-on-primary, #fff);
            box-shadow: none;
          }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'DiscoverAmenities',
  displayName: 'Discover Amenities',
  tokens: {
    'discoveramenities.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Discover All The Amenities</p>',
    },
    'discoveramenities.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_amenities_blurb',
      defaultValue:
        '<p>Our community offers premium amenities to enhance your lifestyle. Start your day with a workout in the fitness center, then relax by the resort-style pool or enjoy the landscaped courtyards. Experience comfort, convenience, and style — your ideal home.</p>',
    },
  },
  slotGroups: {
    'discoveramenities.image': imageDef,
    'discoveramenities.cta': ctaDef,
  },
  collectionBindings: {
    'discoveramenities.items': {
      label: 'Amenities',
      collection: 'amenities',
    },
  },
};

(DiscoverAmenities as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default DiscoverAmenities;
